import { useEffect, useMemo } from 'react';

import { getMsUntilNextSecond, seed } from '#shared';

import type { ActiveCase } from '#entities/types';

import { usePollingQuery, useAggregateRefetch } from '#shared/api';
import type { PollingQueryResult } from '#shared/api';

import { useGetActiveCasesQuery } from '../activeCases.api';

const TARGET_SECOND = 5;

const getActiveCasesDelay = () => getMsUntilNextSecond(TARGET_SECOND);

/**
 * `getActiveCasesDelay` schedules against the next occurrence of `TARGET_SECOND`
 * within a minute, so the fallback poll cycles every 60s aligned to second :05.
 * Exported so a freshness threshold can be expressed in missed refreshes of
 * THIS cadence rather than a disconnected literal.
 */
export const CASES_POLL_MS = 60_000;

// DEFAULT ON; REACT_APP_EVENT_DRIVEN=false reverts to legacy timer polling. When
// on, WS entity frames keep data live so polling is neutralized via `skip` (rules-of-hooks forbids a conditional hook call).
const DEFAULT_EVENT_DRIVEN = import.meta.env.REACT_APP_EVENT_DRIVEN !== 'false';

export interface ActiveCasesPollingResult extends PollingQueryResult<
  ActiveCase[]
> {
  /**
   * `data !== undefined` at the RTK Query cache — true even for an empty array.
   * An empty active-cases list is real, current data; this stays false only
   * before the FIRST payload has ever arrived.
   */
  hasData: boolean;
}

export const useQueryActiveCases = (
  eventDriven: boolean = DEFAULT_EVENT_DRIVEN,
): ActiveCasesPollingResult => {
  const queryResult = useGetActiveCasesQuery(undefined, {
    pollingInterval: 0,
  });

  const { getDelay, refetchSignal } = useAggregateRefetch(
    'activeCases',
    getActiveCasesDelay,
  );
  const pollingResult = usePollingQuery(queryResult, {
    getDelay,
    refetchSignal,
    skip: eventDriven,
  });

  useEffect(() => {
    if (!eventDriven) {
      return;
    }
    const cursor = pollingResult.data?.cursor;
    if (cursor) {
      seed('case', [cursor]);
    }
  }, [eventDriven, pollingResult.data]);

  return useMemo(
    () => ({
      data: pollingResult.data?.data ?? [],
      generatedAt: pollingResult.data?.generatedAt ?? null,
      validation: pollingResult.data?.validation ?? null,
      isLoading: pollingResult.isLoading,
      isFetching: pollingResult.isFetching,
      isError: pollingResult.isError,
      error: pollingResult.error,
      lastSentTime: pollingResult.lastSentTime,
      lastReceiveTime: pollingResult.lastReceiveTime,
      hasData: pollingResult.data !== undefined,
    }),
    [pollingResult],
  );
};
